import React from 'react';
import { FiInfo, FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import { Callout, calloutVariants } from '@components/Callout';
import { GuideStep } from '@components/GuideStep';
import { Accordion } from '@components/Accordion';
import { CopyIconButton } from '@components/CopyIconButton';
import { addNotification, notificationStates } from '@components/Notification';
import styles from './UninstallGuidePage.module.scss';

const INSTALL_PATH = '~/Applications/WfhGo';

const VERIFICATION_STEPS = [
  'Open Activity Monitor.',
  'Select View, then All Processes.',
  'Search for WfhGo.',
  'Confirm that WfhGoApp and WfhGoSvc are no longer running.',
];

export const UninstallGuidePageComponent = () => {
  const handleCopied = (label) => {
    addNotification({
      message: `${label} copied to clipboard`,
      level: notificationStates.success,
      autoDismiss: 2,
    });
  };

  const handleCopyError = () => {
    addNotification({
      message: 'Could not copy to clipboard',
      level: notificationStates.error,
      autoDismiss: 3,
    });
  };

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        <p className={styles.intro}>
          Follow the steps below to completely remove the WfhGo desktop app from
          a Mac.
        </p>

        <Callout
          variant={calloutVariants.info}
          icon={<FiInfo size={14} />}
          title="Before you begin"
        >
          Make sure you&apos;re signed in to the Mac account where WfhGo was
          installed. If you need to retain any logs, save them before starting
          the removal process.
        </Callout>

        <div className={styles.stepsList}>
          <GuideStep number={1} title="Open Finder">
            <p className={styles.stepText}>Open Finder on your Mac.</p>
          </GuideStep>

          <GuideStep number={2} title="Open the WfhGo installation folder">
            <p className={styles.stepText}>
              From the menu bar at the top of the screen, select{' '}
              <strong>Go</strong>, then select <strong>Go to Folder</strong>.
              Enter the following path:
            </p>
            <div className={styles.pathBox}>
              <code className={styles.pathText}>{INSTALL_PATH}</code>
              <CopyIconButton
                value={INSTALL_PATH}
                label="Folder path"
                onCopied={handleCopied}
                onError={handleCopyError}
              />
            </div>
            <p className={styles.stepText}>
              Press Return to open the folder. If WfhGo was installed in a
              different location, open that installation folder instead.
            </p>
          </GuideStep>

          <GuideStep number={3} title="Open the uninstaller">
            <p className={styles.stepText}>
              Inside the WfhGo folder, locate <strong>UninstallWfhGoApp</strong>{' '}
              and double-click it.
            </p>
          </GuideStep>

          <GuideStep number={4} title="Start the uninstallation">
            <p className={styles.stepText}>
              When the uninstallation window appears, select{' '}
              <strong>Uninstall</strong>.
            </p>
            <Callout
              variant={calloutVariants.warning}
              icon={<FiAlertTriangle size={13} />}
              title="Important"
            >
              Do not use uninstall.sh or delete WfhGo files manually.
            </Callout>
          </GuideStep>

          <GuideStep number={5} title="Confirm removal">
            <p className={styles.stepText}>
              Wait for the uninstallation process to finish. The confirmation
              dialog should state that WfhGoApp was removed. Select{' '}
              <strong>OK</strong>.
            </p>
          </GuideStep>
        </div>

        <Accordion
          heading="Optional: verify WfhGo has been completely removed"
          isOpenOnStart={false}
          className={styles.verificationAccordion}
        >
          <ul className={styles.checklist}>
            {VERIFICATION_STEPS.map((item) => (
              <li key={item} className={styles.checklistItem}>
                <FiCheckCircle size={15} className={styles.checklistIcon} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Accordion>

        <Callout variant={calloutVariants.info} title="Important information">
          Ejecting or deleting the WfhGo DMG file does not uninstall the
          application. The DMG can be retained if WfhGo needs to be installed
          again.
        </Callout>

        <Callout
          variant={calloutVariants.info}
          title="For a fully clean reinstall"
        >
          <p>
            If you are preparing the Mac for a fully clean reinstall test, Apple
            may retain the WfhGoAgent Screen Recording permission after
            uninstallation.
          </p>
          <p>
            Open <strong>System Settings</strong>, go to{' '}
            <strong>Privacy &amp; Security</strong>, then{' '}
            <strong>Screen &amp; System Audio Recording</strong>. Select{' '}
            <strong>WfhGoAgent</strong> and remove its permission using the
            minus button. Authenticate if requested. If WfhGoAgent is not
            listed, no further action is required.
          </p>
          <p>Leave permissions for other applications unchanged.</p>
        </Callout>
      </div>
    </div>
  );
};
